import { useState } from 'react';
import { Link, NavLink, Outlet } from 'react-router-dom';
import { Wordmark } from './Wordmark';
import { cn } from '@/lib/cn';

// Real multi-page nav. No more anchor links.
const NAV = [
  { href: '/',              label: 'Home' },
  { href: '/tools',         label: 'AI Tools' },
  { href: '/academy',       label: 'Academy' },
  { href: '/features',      label: 'Features' },
  { href: '/for-districts', label: 'For Districts' },
  { href: '/pricing',       label: 'Pricing' },
  { href: '/about',         label: 'About' },
];

const FOOTER_COLUMNS = [
  {
    title: 'Product',
    links: ['Features', 'AI Tools', 'Pricing', 'What\'s New', 'Roadmap', 'API'],
  },
  {
    title: 'Resources',
    links: ['Help Center', 'Video Tutorials', 'Blog', 'Community Forum', 'Teacher Stories', 'Webinars'],
  },
  {
    title: 'Company',
    links: ['About Us', 'Careers', 'Press Kit', 'Contact Sales', 'Privacy Policy', 'Terms of Service'],
  },
];

function NavLinkItem({ href, label }) {
  return (
    <NavLink
      to={href}
      end={href === '/'}
      className={({ isActive }) =>
        cn(
          'transition-colors font-medium',
          isActive ? 'text-purple-600' : 'text-gray-700 hover:text-purple-600',
        )
      }
    >
      {label}
    </NavLink>
  );
}

export default function MarketingLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <div className="flex min-h-screen flex-col bg-white">
      <nav className="px-6 py-4 border-b border-purple-100 bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <Link to="/" aria-label="Spark Nation home">
            <Wordmark size="lg" />
          </Link>

          <div className="hidden lg:flex items-center gap-5">
            {NAV.map((n) => <NavLinkItem key={n.href} {...n} />)}
          </div>
          <div className="hidden lg:flex items-center gap-3">
            <a
              href={import.meta.env.VITE_LMS_URL || 'http://localhost:5174'}
              className="px-5 py-2 border border-purple-600 text-purple-600 rounded-full hover:bg-purple-50 transition-all font-semibold"
            >
              Sign In
            </a>
            <Link
              to="/contact?reason=demo"
              className="px-5 py-2 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-full hover:shadow-lg transition-all font-semibold"
            >
              Request Demo
            </Link>
          </div>

          <button
            className="lg:hidden p-2 text-gray-700"
            onClick={() => setMobileOpen(!mobileOpen)}
            aria-label="Toggle navigation"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>
        </div>
        {mobileOpen && (
          <div className="lg:hidden mt-4 pb-4 space-y-3">
            {NAV.map((n) => (
              <NavLink
                key={n.href}
                to={n.href}
                end={n.href === '/'}
                onClick={() => setMobileOpen(false)}
                className={({ isActive }) => cn(
                  'block py-2 font-medium transition-colors',
                  isActive ? 'text-purple-600' : 'text-gray-700 hover:text-purple-600',
                )}
              >
                {n.label}
              </NavLink>
            ))}
            <a href={import.meta.env.VITE_LMS_URL || 'http://localhost:5174'} onClick={() => setMobileOpen(false)} className="block w-full px-6 py-2 border border-purple-600 text-purple-600 rounded-full hover:bg-purple-50 transition-all text-center font-semibold">
              Sign In
            </a>
            <Link to="/contact?reason=demo" onClick={() => setMobileOpen(false)} className="block w-full px-6 py-2 bg-gradient-to-r from-purple-600 to-purple-500 text-white rounded-full text-center font-semibold">
              Request Demo
            </Link>
          </div>
        )}
      </nav>

      <main className="flex-1">
        <Outlet />
      </main>

      <footer className="px-6 py-12 bg-gray-900 text-gray-300">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-8 mb-12">
            <div className="md:col-span-2">
              <Wordmark size="md" variant="white" />
              <p className="text-sm mt-4 mb-6 leading-relaxed text-gray-400">
                Empowering educators worldwide with AI-powered tools for modern learning. Save time,
                inspire creativity, and transform your teaching experience.
              </p>
              <div className="flex gap-4">
                {['𝕏', 'in', 'f', 'yt'].map((icon) => (
                  <a
                    key={icon}
                    href="#"
                    className="w-10 h-10 bg-gray-800 rounded-full flex items-center justify-center hover:bg-purple-600 transition-colors text-white"
                  >
                    <span className="text-sm font-bold">{icon}</span>
                  </a>
                ))}
              </div>
            </div>
            {FOOTER_COLUMNS.map((col) => (
              <div key={col.title}>
                <h4 className="text-white font-semibold mb-4">{col.title}</h4>
                <div className="space-y-2 text-sm">
                  {col.links.map((label) => (
                    <div key={label}>
                      <a href="#" className="hover:text-white transition-colors">{label}</a>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
          <div className="pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm">&copy; {new Date().getFullYear()} Spark Nation AI. All rights reserved.</p>
            <div className="flex gap-6 text-sm">
              {['Privacy', 'Terms', 'Security', 'Status'].map((l) => (
                <a key={l} href="#" className="hover:text-white transition-colors">{l}</a>
              ))}
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
